import React from 'react';
import { FiGlobe, FiTrendingUp,  FiBarChart2, FiUsers, FiShield, FiClock, FiZap } from 'react-icons/fi';

const TrustManagement = () => {
  const benefits = [
    {
      icon: <FiGlobe className="w-6 h-6" />,
      title: 'Complete Visibility Across All Institutions',
      description: 'Trustees and administrators finally have a single window into every institution under their umbrella. See performance metrics, attendance trends, financial health, and operational data across schools, colleges, and universities—all in one place. No more piecing together reports from different systems.',
      color: 'bg-indigo-50'
    },
    {
      icon: <FiTrendingUp className="w-6 h-6" />,
      title: 'Standardized Processes with Local Flexibility',
      description: 'Get the consistency you need without losing the flexibility each institution requires. Define trust-wide policies that automatically apply everywhere, while allowing each principal or director to manage their institution their way. The best of both worlds.',
      color: 'bg-emerald-50'
    },
    {
      icon: <FiBarChart2 className="w-6 h-6" />,
      title: 'Data-Driven Decision Making',
      description: 'Trusts run on decisions—where to open a new campus, which programs to expand, which institutions need support. With unified data, your decisions are backed by real insights, not intuition. Compare performance across institutions, identify best practices, and make strategic moves with confidence.',
      color: 'bg-amber-50'
    },
    {
      icon: <FiUsers className="w-6 h-6" />,
      title: 'Resource Optimization Across Institutions',
      description: 'Share resources intelligently across your trust. If one campus has excess capacity in a subject, another can benefit. Faculty can be deployed where needed most. Infrastructure can be optimized. Your entire trust becomes more efficient when you can see and manage resources at scale.',
      color: 'bg-rose-50'
    },
    {
      icon: <FiShield className="w-6 h-6" />,
      title: 'Enhanced Brand Trust and Reputation',
      description: 'Consistent quality across all institutions builds trust. Parents, students, and staff know that every institution under your trust maintains high standards. A unified ERP helps you maintain those standards everywhere, strengthening your brand reputation in the community.',
      color: 'bg-purple-50'
    },
    {
      icon: <FiClock className="w-6 h-6" />,
      title: 'Reduced Administrative Overhead',
      description: 'No more duplicate efforts across institutions. Student data flows seamlessly. Reports are generated automatically. Staff training is standardized. The administrative burden across your trust drops significantly, freeing up time for what really matters—education.',
      color: 'bg-blue-50'
    }
  ];

  return (
    <>
      {/* Introduction */}
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        Educational trusts are the architects of India's education system. A single trust might run ten schools, five colleges, and a university. Each institution serves different communities, follows different academic calendars, and has its own unique character. For trustees and administrators, managing this ecosystem is both a responsibility and a challenge.
      </p>
      
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        The dream of the trust is simple: <strong className="text-slate-900">quality education across every institution, consistent standards, and efficient operations.</strong> But the reality is often different. Separate systems. Disconnected data. Inconsistent processes. Administrative burnout. And a constant feeling that you're not seeing the full picture.
      </p>

      <p className="text-lg text-slate-600 leading-relaxed mb-8">
        A unified ERP platform changes all of that. Here's how it helps educational trusts manage multiple institutions seamlessly.
      </p>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-indigo-600">50%</div>
          <div className="text-sm text-slate-500">Reduction in admin work with unified ERP</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-amber-600">100%</div>
          <div className="text-sm text-slate-500">Visibility across all institutions</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-emerald-600">3x</div>
          <div className="text-sm text-slate-500">Faster decision making with unified data</div>
        </div>
      </div>

      <h2 className="text-2xl font-bold text-slate-900 mb-6">What a Unified ERP Delivers for Educational Trusts</h2>

      {/* Benefits List */}
      {benefits.map((item, i) => (
        <div key={i} className={`${item.color} rounded-2xl p-6 mb-6 border`}>
          <div className="flex items-start gap-4">
            <div className="bg-white rounded-xl p-3 shadow-sm flex-shrink-0 mt-1">
              {item.icon}
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-slate-900 mb-2">{item.title}</h3>
              <p className="text-slate-600 leading-relaxed text-sm">{item.description}</p>
            </div>
          </div>
        </div>
      ))}

      {/* The Real Impact Section */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">The Real Impact on Your Trust</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
            <div className="text-sm font-semibold text-blue-700 mb-2">Before Unified ERP</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>Separate systems for each institution</span></li>
              <li className="flex items-start gap-2">• <span>Data doesn't sync across campuses</span></li>
              <li className="flex items-start gap-2">• <span>No central view of performance</span></li>
              <li className="flex items-start gap-2">• <span>Inconsistent processes everywhere</span></li>
              <li className="flex items-start gap-2">• <span>Manual report generation takes weeks</span></li>
              <li className="flex items-start gap-2">• <span>Staff trained on multiple systems</span></li>
            </ul>
          </div>
          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
            <div className="text-sm font-semibold text-emerald-700 mb-2">After Truyon Unified ERP</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>One platform for all institutions</span></li>
              <li className="flex items-start gap-2">• <span>Real-time data sync across campuses</span></li>
              <li className="flex items-start gap-2">• <span>Complete central visibility</span></li>
              <li className="flex items-start gap-2">• <span>Consistent processes with local flexibility</span></li>
              <li className="flex items-start gap-2">• <span>Reports generated instantly</span></li>
              <li className="flex items-start gap-2">• <span>Single training program for all staff</span></li>
            </ul>
          </div>
        </div>
      </div>

      {/* Key Features Section */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6">
        <h3 className="text-lg font-bold text-slate-900 mb-3">Key Features for Trust Management</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="bg-indigo-50 rounded-xl p-4 border border-indigo-200">
            <FiGlobe className="w-6 h-6 text-indigo-600 mb-2" />
            <h4 className="font-bold text-slate-900 text-sm">Central Dashboard</h4>
            <p className="text-xs text-slate-600">See all your institutions at a glance. Performance metrics, attendance, finances, and more.</p>
          </div>
          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
            <FiBarChart2 className="w-6 h-6 text-emerald-600 mb-2" />
            <h4 className="font-bold text-slate-900 text-sm">Cross-Institution Reports</h4>
            <p className="text-xs text-slate-600">Compare performance across institutions. Identify trends and best practices.</p>
          </div>
          <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
            <FiZap className="w-6 h-6 text-amber-600 mb-2" />
            <h4 className="font-bold text-slate-900 text-sm">Automated Workflows</h4>
            <p className="text-xs text-slate-600">Standardize processes across institutions. Reduce manual work and errors.</p>
          </div>
          <div className="bg-rose-50 rounded-xl p-4 border border-rose-200">
            <FiUsers className="w-6 h-6 text-rose-600 mb-2" />
            <h4 className="font-bold text-slate-900 text-sm">Central User Management</h4>
            <p className="text-xs text-slate-600">Manage staff across all institutions from one place. Simplify access control.</p>
          </div>
          <div className="bg-purple-50 rounded-xl p-4 border border-purple-200">
            <FiShield className="w-6 h-6 text-purple-600 mb-2" />
            <h4 className="font-bold text-slate-900 text-sm">Unified Security</h4>
            <p className="text-xs text-slate-600">Enterprise-grade security across all institutions. Protect student and staff data.</p>
          </div>
          <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
            <FiTrendingUp className="w-6 h-6 text-blue-600 mb-2" />
            <h4 className="font-bold text-slate-900 text-sm">Growth Analytics</h4>
            <p className="text-xs text-slate-600">Identify growth opportunities across your trust. Data-backed expansion decisions.</p>
          </div>
        </div>
      </div>

      {/* The Human Story */}
      <div className="mt-8 bg-amber-50 rounded-2xl p-6 border border-amber-200">
        <h3 className="text-lg font-bold text-amber-900 mb-3">The Human Side of Trust Management</h3>
        <p className="text-amber-800 leading-relaxed mb-3">
          Behind every trust are real people—trustees who want to make a difference, administrators who work tirelessly to keep things running, teachers who inspire students every day, and students whose futures depend on the quality of education they receive.
        </p>
        <p className="text-amber-800 leading-relaxed mb-3">
          A unified ERP isn't just about technology. It's about freeing up human potential. It's about giving trustees the clarity they need to make bold decisions. It's about reducing the administrative burden so teachers can focus on teaching. It's about ensuring every student, in every institution, gets the quality education they deserve.
        </p>
        <p className="text-amber-800 leading-relaxed">
          <strong>Technology should serve people, not the other way around.</strong> That's what a unified ERP does—it serves the people who serve education.
        </p>
      </div>

      {/* Conclusion */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-2">One Platform. All Your Institutions. Complete Control.</h3>
        <p className="text-indigo-800 leading-relaxed">
          Educational trusts are building the future of India—one institution at a time. The complexity of managing multiple institutions should not hold you back. With a unified ERP, you get the visibility, consistency, and efficiency you need to focus on what matters most: quality education.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          Truyon is built for trusts. One platform that respects the individuality of each institution while giving you the unified view you need. Complete visibility. Consistent quality. Effortless management.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          <strong>Your trust manages multiple institutions. Your ERP should too.</strong>
        </p>
      </div>
    </>
  );
};

export default TrustManagement;